import { toast } from 'react-hot-toast';
import { useLocalStorage } from '../helpers/useLocalStorage';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bug,
  ClipboardList,
  Copy,
  Gauge,
  CornerDownRight,
  TrendingUp,
} from 'lucide-react';
import { ChevronDownIcon, ChevronUpIcon, EditIcon } from '@iconicicons/react';
import { Button, UncontrolledTooltip } from 'reactstrap';
import { numberFormatter } from '../helpers/formatters';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import {
  useGetCapacityComponentBulkHistoryQuery,
  useGetCapacityComponentQuery,
} from '../services/edgeApi';
import { useSession } from '../hooks/useSession';
import { colors, generateColor, interpolateColor } from '../utils/colors';
import { EditCapacityComponentModal } from '../capacityComponents/EditCapacityComponentModal';
import {
  PartialRecord,
  SegmentFlowDirection,
  PointFlowDirection,
  ArrayElement,
} from '@/types';
import type { NomCycle } from '@/types';
import {
  PathEvaluatedFlowRecord,
  PathFlowRow,
  PathFlows,
  PathNoticeSliceRecord,
} from '@/types/api/CapacityComponent';
import { NoticeRecordListItem } from '@/types/api/NoticeRecord';
import Plot from 'react-plotly.js';
import { useTheme } from '../helpers/hooks';
import { Data, Dash } from 'plotly.js';
import { titleize } from '../utils/stringUtils';
import { MultiSelectButton } from '../helpers/MultiSelect';
import { HorizontalDivider } from '../helpers/HorizontalDivider';
import {
  FlowDashboardFormattingSettings,
  FlowDashboardHistoryViewSettings,
  formatFlowDashboardVolume,
} from './flowDashboardStorage';
import { MaintenanceNoticeTooltip } from '../maintenance/MaintenanceNoticeTooltip';
import { NoticeRecordModal } from '../notices/NoticeRecordModal';

const START_COLOR = '#0096FF';
const END_COLOR = '#FF0000';

type BulkFlowTableRow = PathFlowRow & {
  _groupKey: string;
  _isRollupParent?: boolean;
  _isRawChild?: boolean;
  _isNoticeChild?: boolean;
  _childCount?: number;

  rowKey: string;
  domSafeRowKey: string;
  componentId: number;
  label: string;
  flowMap: PartialRecord<string, PathEvaluatedFlowRecord>;
  noticeMap: PartialRecord<string, PathNoticeSliceRecord[]>;
  rawFlowDirection: PointFlowDirection | SegmentFlowDirection | null;
  noticeDirection: PointFlowDirection | SegmentFlowDirection | null;
};

const FLOW_DIRECTION_SHORT: PartialRecord<
  PointFlowDirection | SegmentFlowDirection,
  string
> = {
  RECEIPT: 'R',
  DELIVERY: 'D',
  FORWARDHAUL: 'FH',
  BACKHAUL: 'BH',
};

const MAP_BUILDER_PERMISSIONS = [
  'change_point',
  'change_edge',
  'change_interconnect',
  'change_node',
];

const POOLING_FUNCTIONS = {
  min: Math.min,
  max: Math.max,
};

const POOLING_TEXTS: Record<string, string> = {
  min: 'Minimum',
  max: 'Maximum',
};

const toDirectionShort = (
  direction: PointFlowDirection | SegmentFlowDirection | null | undefined,
) => {
  if (!direction) return '';
  return FLOW_DIRECTION_SHORT[direction] || direction;
};

// type PlotlyDash = PlotData['line']['dash'];

// Fixed nomination order for the grid
const NOM_CYCLES: [NomCycle, NomCycle, NomCycle, NomCycle, NomCycle] = [
  'T',
  'E',
  'I1',
  'I2',
  'I3',
];
const reversedCycles: NomCycle[] = [...NOM_CYCLES].reverse() as NomCycle[];
const NOM_CYCLE_LINE_MODE: Record<NomCycle, Dash> = {
  T: 'dot',
  E: 'longdash',
  I1: 'longdashdot',
  I2: 'dash',
  I3: 'solid',
};
const NOM_CYCLE_OPACITY: Record<NomCycle, number> = {
  T: 0.5,
  E: 0.6,
  I1: 0.7,
  I2: 0.8,
  I3: 1,
};
const NOM_CYCLE_COLOR: Record<NomCycle, string> = {
  T: colors.mattePurple,
  E: colors.matteBlue,
  I1: colors.matteGreen,
  I2: colors.matteYellow,
  I3: colors.matteRed,
};

// Build date range backwards from endDate.
function generateReverseDates(
  endDate: string,
  limit: number,
  startDate?: string,
) {
  const [y, m, d] = endDate.split('-').map(Number);
  let cur = new Date(Date.UTC(y, m - 1, d));
  const dates: string[] = [];
  while (dates.length <= limit) {
    const out = cur.toISOString().slice(0, 10);
    if (startDate && out < startDate) break;
    dates.push(out);
    cur = new Date(cur.getTime() - 86400 * 1000);
  }
  return dates;
}
const DEFAULT_END_GAS_DATE = new Date().toISOString().slice(0, 10);

export type CapacityComponentBulkHistoryProps = {
  componentIds: number[] | null;
  data?: PathFlows; // Optional pre-fetched data to avoid re-fetching
  limit?: number;
  endGasDate?: string;
  startGasDate?: string;
  hasMoreHistory?: boolean;
  isLoadingMoreHistory?: boolean;
  onLoadMoreHistory?: () => void;
  onHistoryScopeChange?: (scope: 'focused' | 'history') => void;
  conceptStorageKey?: string;
  componentLabelMode?: 'label' | 'short_label';
  showTspName?: boolean;
  flowDashboardFormatting?: FlowDashboardFormattingSettings;
  historyViewSettings?: FlowDashboardHistoryViewSettings;
  cycleColumnMode?: 'all_cycles' | 'any_record' | 'flow_record';
  onSave?: () => void | Promise<void>;
};

export const CapacityComponentBulkHistory: React.FC<
  CapacityComponentBulkHistoryProps
> = ({
  componentIds,
  data: preFetchedData,
  limit: limitProp,
  endGasDate,
  startGasDate,
  hasMoreHistory = false,
  isLoadingMoreHistory = false,
  onLoadMoreHistory,
  onHistoryScopeChange,
  conceptStorageKey = 'path_flow_concept',
  componentLabelMode = 'label',
  showTspName = true,
  flowDashboardFormatting,
  historyViewSettings,
  cycleColumnMode = 'all_cycles',
  onSave,
}) => {
  const resolvedLimit = limitProp ?? 30;
  const resolvedEndGasDate = endGasDate || DEFAULT_END_GAS_DATE;
  const [theme] = useTheme();
  const { permissions = [] } = useSession();
  // Unless we are using this for another reason, we could probably call it PathFlowHistory
  const {
    data: fetchedData,
    error,
    isLoading,
  } = useGetCapacityComponentBulkHistoryQuery(
    {
      componentIds,
      offset: resolvedEndGasDate,
      limit: resolvedLimit,
    },
    {
      skip: !!preFetchedData, // Skip fetching if data is provided
    },
  );
  const [chartOpen, setChartOpen] = useLocalStorage<boolean>(
    'bulkFlowHistoryChartOpen',
    false,
  );

  const toggleChart = useCallback(() => {
    setChartOpen(!chartOpen);
  }, [chartOpen]);

  // Use pre-fetched data if available, otherwise use fetched data
  const data = preFetchedData || fetchedData;

  // user-selected concept (existing logic)
  const [concept, setConcept] = useLocalStorage<keyof PathEvaluatedFlowRecord>(
    conceptStorageKey,
    'flowable_volume',
  );
  const [debug, setDebug] = useState(false);
  const [chartMode, setChartMode] = useState<'raw' | 'min' | 'max'>('min');
  const [selectedSliceKey, setSelectedSliceKey] = useState<string | null>(null);
  const [historyScope, setHistoryScope] = useState<'focused' | 'history'>(
    historyViewSettings?.defaultScope ?? 'history',
  );
  const [focusedDate, setFocusedDate] = useState<string | null>(null);
  const [focusedCycle, setFocusedCycle] = useState<'all' | NomCycle>(
    historyViewSettings?.focusedCycles === 'pinned'
      ? historyViewSettings.pinnedCycle
      : 'all',
  );
  const [expandedGroups, setExpandedGroups] = useState<
    PartialRecord<string, boolean>
  >({});
  const [editingComponentId, setEditingComponentId] = useState<number | null>(
    null,
  );
  const containerRef = React.useRef<HTMLDivElement>(null);
  const matrixScrollRef = React.useRef<HTMLDivElement>(null);
  const studyPickerScrollRef = React.useRef<HTMLDivElement>(null);
  const [lastMatrixScrollLeft, setLastMatrixScrollLeft] = useState(0);

  const { data: editingComponent } = useGetCapacityComponentQuery(
      { componentId: editingComponentId ?? 0 },
      { skip: editingComponentId === null },
    );

  const groupedRows = Array.isArray(data?.groups) ? data!.groups : [];
  const canSeeMapBuilder = MAP_BUILDER_PERMISSIONS.every((permission) =>
    permissions.includes(permission),
  );

  if (!groupedRows || groupedRows.length === 0) {
    return <div>No history found for selected components.</div>;
  }

  const gasDates = generateReverseDates(
    resolvedEndGasDate,
    resolvedLimit,
    startGasDate,
  );
  const toDomSafeToken = (value: string | number | null | undefined) =>
    (value?.toString() ?? '').replace(/[^A-Za-z0-9_-]/g, '_');

  const cycleButtonId = (sliceKey: string) =>
    `cycle_button_${toDomSafeToken(sliceKey)}`;

  const parseSliceKey = (sliceKey: string) => {
    const [sliceDate, sliceCycle] = sliceKey.split('_');
    return { sliceDate, sliceCycle };
  };
  const [selectedNoticeRecordId, setSelectedNoticeRecordId] = useState<number | null>(null);

  const buildRow = (
    row: PathFlowRow,
    key: string,
    groupId: string,
  ): BulkFlowTableRow => {
    const flowMap: PartialRecord<string, PathEvaluatedFlowRecord> = {};
    const noticeMap: PartialRecord<
      string,
      ArrayElement<PathFlowRow['notice_slice']['records_with_date_cycle']>[]
    > = {};

    (row.flows || []).forEach((f) => {
      flowMap[`${f.gas_date}_${f.nomination_cycle}`] = f;
    });

    (row.notice_slice?.records_with_date_cycle || []).forEach((record) => {
      const key = `${
        'gas_date' in record ? record.gas_date : record.start_gas_date
      }_${record.nomination_cycle}`;
      if (!noticeMap[key]) {
        noticeMap[key] = [];
      }
      noticeMap[key]!.push(record);
    });

    const rowKey = `${groupId}__${row.row_role}__${row.source_kind}__${key}`;
    const noticeDirection =
      row.notice_slice?.notice_component?.flow_direction || null;

    return {
      ...row,
      rowKey,
      domSafeRowKey: toDomSafeToken(rowKey),
      componentId: row.component_id,
      label: row.label,
      flowMap,
      noticeMap,
      rawFlowDirection:
        row.raw_flow_direction ||
        row.flows?.find((f) => f?.flow_direction)?.flow_direction ||
        null,
      noticeDirection,
      _groupKey: groupId,
    };
  };

  const getDisplayRowLabel = (row: BulkFlowTableRow): string => {
    if (
      componentLabelMode === 'short_label' &&
      !row._isRawChild &&
      !row._isNoticeChild
    ) {
      return row.short_label || row.label;
    }
    return row.label;
  };

  const muteColor = (hexColor: string, amount: number = 0.35) => {
    return interpolateColor(hexColor, '#808080', amount);
  };

  const getColor = (
    concept: string,
    record: PathEvaluatedFlowRecord | undefined,
  ) => {
    const noColor = 'var(--bs-table-bg)';
    if (!record) return noColor;

    if (
      flowDashboardFormatting?.showLowFlowAlert &&
      record.flowable_volume != null &&
      record.flowable_volume < flowDashboardFormatting.lowFlowThreshold
    ) {
      return flowDashboardFormatting.lowFlowAlertColor;
    }

    if (flowDashboardFormatting && !flowDashboardFormatting.showUtilizationGradient) {
      return noColor;
    }

      if (record.utilization == null) return noColor;

    let color;
    if (concept === 'interruptible_flow') {
      // @ts-ignore
      color = record.interruptible_flow === 'Y' ? START_COLOR : END_COLOR;
    } else {
      color = interpolateColor(
        START_COLOR,
        END_COLOR,
        Math.min(record?.utilization, 1),
      );
    }
    return muteColor(color, 0.35);
  };

  const formatConcept = (
    concept: string,
    value: boolean | string | number | null | undefined,
    sign?: number,
  ) => {
    if (value === null || value === undefined) return '-';

    if (Number(value) !== value) {
      return value;
    }

    if (['scheduled_volume', 'opcap', 'oac'].includes(concept)) {
      return numberFormatter((sign || 1) * value, 0);
    }
    // Don't multiply flowable_volume by sign
    if (['flowable_volume'].includes(concept)) {
      if (flowDashboardFormatting) {
        return formatFlowDashboardVolume(value, flowDashboardFormatting);
      }
      return numberFormatter(value, 0);
    }

    if (concept === 'utilization') {
      return `${numberFormatter(100 * (sign || 1) * value, 0)}%`;
    }

    return value;
  };

  const getRenderableNoticeRecords = (
    records: PathNoticeSliceRecord[] = [],
  ): NoticeRecordListItem[] =>
    records.filter((record): record is NoticeRecordListItem => 'id' in record);

  const renderNoticeTooltip = (records: NoticeRecordListItem[]) => (
    <div className="text-start">
      {records.map((record, idx) => (
        <div
          key={record.id ?? idx}
          className={idx > 0 ? 'mt-2 pt-2 border-top' : ''}
        >
          <MaintenanceNoticeTooltip notice={record} />
        </div>
      ))}
    </div>
  );

  const getNoticeSummaryText = (records: NoticeRecordListItem[]) => {
    const texts = records
      .map((record) => record.display_values?.text?.trim())
      .filter((value): value is string => Boolean(value));

    if (texts.length === 0) {
      return '';
    }

    return Array.from(new Set(texts)).join(' / ');
  };

  const renderNoticeDot = () => (
    <span
      className="d-inline-block rounded-circle bg-white border border-dark-subtle"
      style={{ width: 7, height: 7 }}
      title="Has notice details"
    />
  );

  const rolledRows = useMemo<BulkFlowTableRow[]>(() => {
    const out: BulkFlowTableRow[] = [];

    groupedRows.forEach((group, groupIndex) => {
      const groupKey =
        group.group_id || `${group.component_id}|${group.path_direction}`;
      const parent = buildRow(
        group.parent_row || {},
        `parent_${groupIndex}`,
        groupKey,
      );
      const children = (group.evidence_rows || []).map((child, idx) =>
        buildRow(child, `child_${groupIndex}_${idx}`, groupKey),
      );

      const flowChildren = children.filter(
        (child) => child.child_kind === 'raw_flow',
      );
      const noticeChildren = children.filter(
        (child) => child.source_kind === 'notice',
      );
      const visibleFlowChildren = debug
        ? flowChildren
        : flowChildren.filter((child) => child.visible);
      const visibleNoticeChildren = debug
        ? noticeChildren
        : noticeChildren.filter((child) => child.visible);

      const allChildren = [...visibleFlowChildren, ...visibleNoticeChildren];

      if (!debug && !parent.visible) {
        return;
      }

      const rolledNoticeMap: PartialRecord<string, PathNoticeSliceRecord[]> =
        {};
      (debug
        ? noticeChildren
        : noticeChildren.filter((g) => g.visible)
      ).forEach((noticeRow) => {
        Object.entries(noticeRow.noticeMap || {}).forEach(([key, records]) => {
          if (!records || records.length === 0) return;
          if (!rolledNoticeMap[key]) {
            rolledNoticeMap[key] = [];
          }
          rolledNoticeMap[key]!.push(...records);
        });
      });

      const isExpanded = Boolean(expandedGroups[groupKey]);
      const item = {
        ...parent,
        _groupKey: groupKey,
        _isRollupParent: true,
        _isRawChild: false,
        _isNoticeChild: false,
        _childCount: allChildren.length,
        noticeMap: rolledNoticeMap,
      };

      out.push(item);

      if (isExpanded) {
        allChildren.forEach((child) => {
          const childItem = {
            ...child,
            _groupKey: groupKey,
            _isRollupParent: false,
            _isRawChild: child.child_kind === 'raw_flow',
            _isNoticeChild: child.source_kind === 'notice',
            _childCount: 0,
          };
          out.push(childItem);
        });
      }
    });

    return out;
  }, [groupedRows, debug, expandedGroups]);

  // Show all scheduled cycles or only cycles represented by the selected record type.
  const activeCyclesByDate = useMemo(() => {
    const result: Record<string, NomCycle[]> = {};
    const scanRows = debug ? rolledRows : rolledRows.filter((r) => r.visible);
    for (const date of gasDates) {
      const present = new Set<NomCycle>();
      for (const row of scanRows) {
        for (const cycle of reversedCycles) {
          const key = `${date}_${cycle}`;
          const record = row.flowMap[key];
          const hasFlowData =
            record != null &&
            (record.oac != null ||
              record.scheduled_volume != null ||
              record.flowable_volume != null);
          const hasNoticeData = getRenderableNoticeRecords(
            row.noticeMap[key] || [],
          ).length > 0;
          if (
            cycleColumnMode === 'all_cycles' ||
            hasFlowData ||
            (cycleColumnMode === 'any_record' && hasNoticeData)
          ) {
            present.add(cycle);
          }
        }
      }
      result[date] = reversedCycles.filter((c) => present.has(c));
    }
    return result;
  }, [rolledRows, gasDates, debug, cycleColumnMode]);

  const getCyclesForDate = (date: string): NomCycle[] =>
    cycleColumnMode === 'all_cycles'
      ? reversedCycles
      : activeCyclesByDate[date]?.length
      ? activeCyclesByDate[date]
      : reversedCycles;

  // In all-cycles mode, retain every requested date even when it has no records.
  const visibleGasDates = useMemo(
    () =>
      cycleColumnMode === 'all_cycles'
        ? gasDates
        : gasDates.filter((date) => (activeCyclesByDate[date]?.length ?? 0) > 0),
    [gasDates, activeCyclesByDate, cycleColumnMode],
  );

  const pendingCycleCount = (date: string): number =>
    reversedCycles.length - getCyclesForDate(date).length;

  useEffect(() => {
    if (
      visibleGasDates.length > 0 &&
      (!focusedDate || !visibleGasDates.includes(focusedDate))
    ) {
      setFocusedDate(visibleGasDates[0]);
    }
  }, [focusedDate, visibleGasDates]);

  useEffect(() => {
    if (!historyViewSettings) {
      return;
    }

    setHistoryScope(historyViewSettings.defaultScope);
    setFocusedCycle(
      historyViewSettings.focusedCycles === 'pinned'
        ? historyViewSettings.pinnedCycle
        : 'all',
    );
  }, [
    historyViewSettings?.defaultScope,
    historyViewSettings?.focusedCycles,
    historyViewSettings?.pinnedCycle,
  ]);

  const isFocusedView =
    historyViewSettings != null && historyScope === 'focused';
  const focusedDateCycles = focusedDate ? getCyclesForDate(focusedDate) : [];
  const displayedGasDates =
    isFocusedView && focusedDate ? [focusedDate] : visibleGasDates;
  const getDisplayedCyclesForDate = (date: string): NomCycle[] => {
    const cycles = getCyclesForDate(date);
    if (
      !isFocusedView ||
      date !== focusedDate ||
      focusedCycle === 'all'
    ) {
      return cycles;
    }
    return cycles.filter((cycle) => cycle === focusedCycle);
  };

  const toClipboardCell = (value: unknown): string =>
    String(value ?? '')
      .replaceAll('\t', ' ')
      .replaceAll('\r', ' ')
      .replaceAll('\n', ' ');

  const getMatrixClipboardValue = (
    row: BulkFlowTableRow,
    date: string,
    cycle: NomCycle,
  ): string | number => {
    const key = `${date}_${cycle}`;
    const noticeRecords = getRenderableNoticeRecords(row.noticeMap?.[key] || []);
    const noticeSummary = getNoticeSummaryText(
      noticeRecords as NoticeRecordListItem[],
    );

    if (row.source_kind === 'notice') {
      return noticeSummary || (noticeRecords.length ? noticeRecords.length : '');
    }

    const record = row.flowMap[key];
    if (!record) {
      return noticeSummary;
    }

    const value = record[concept];
    if (value === null || value === undefined) return '';
    if (typeof value !== 'number') return String(value);
    if (['scheduled_volume', 'opcap', 'oac', 'utilization'].includes(concept)) {
      return (record.direction_sign || 1) * value;
    }
    return value;
  };

  const copyMatrixToClipboard = async () => {
    const slices = displayedGasDates.flatMap((date) =>
      getDisplayedCyclesForDate(date).map((cycle) => ({ date, cycle })),
    );
    const rows = [
      [
        'Component',
        'TSP',
        'Path Direction',
        'Row Type',
        ...slices.map(({ date }) => date),
      ],
      ['', '', '', '', ...slices.map(({ cycle }) => cycle)],
      ...rolledRows.map((row) => [
        getDisplayRowLabel(row),
        row.tsp_name || '',
        toDirectionShort(row.path_direction),
        row._isRawChild
          ? 'Raw Flow'
          : row._isNoticeChild
          ? 'Notice'
          : 'Summary',
        ...slices.map(({ date, cycle }) =>
          getMatrixClipboardValue(row, date, cycle),
        ),
      ]),
    ];
    const tsv = rows
      .map((row) => row.map(toClipboardCell).join('\t'))
      .join('\n');

    try {
      await navigator.clipboard.writeText(tsv);
      toast.success('Flow table copied for Excel');
    } catch {
      toast.error('Unable to copy flow table');
    }
  };
  const laterFocusedCycles =
    focusedCycle === 'all'
      ? []
      : NOM_CYCLES.slice(NOM_CYCLES.indexOf(focusedCycle) + 1).filter((cycle) =>
          focusedDateCycles.includes(cycle),
        );

  const [chartCycles, setChartCycles] = useState<NomCycle[]>(NOM_CYCLES);

  const getRawChartData = useCallback(() => {
    return rolledRows.flatMap<Data>((row, index) => {
      return chartCycles.map<Data>((nomCycle) => {
        const values = gasDates.map((gasDate) => {
          const key = `${gasDate}_${nomCycle}`;
          const value = row.flowMap?.[key]?.[concept];
          return value ? Number(value) : null;
        });
        return {
          x: gasDates,
          y: values,
          type: 'scatter',
          mode: 'lines',
          legendgroup: getDisplayRowLabel(row),
          legendgrouptitle: { text: getDisplayRowLabel(row) },
          visible: index === 0 ? true : 'legendonly',
          opacity: NOM_CYCLE_OPACITY[nomCycle],
          // stackgroup: row.label,
          toast: `Component ${row.componentId} - ${getDisplayRowLabel(
            row,
          )} (${nomCycle})`,
          marker: {
            color:
              theme === 'light'
                ? generateColor(row.componentId || 0)
                : generateColor(row.componentId || 0, 50, 40),
          },
          line: {
            dash: NOM_CYCLE_LINE_MODE[nomCycle],
          },
          hovertemplate:
            `<b>%{x}</b><br>` +
            `<b>Component:</b> ${getDisplayRowLabel(row)}<br>` +
            `<b>Cycle:</b> ${nomCycle}<br>` +
            `<b>${titleize(concept.replaceAll('_', ' '))}:</b> %{y}<br>`,
          name: `${nomCycle}`,
        };
      });
    });
  }, [rolledRows, chartCycles, gasDates, concept, theme]);

  const getAggregatedChartData = useCallback(
    (mode: 'min' | 'max') => {
      const aggregatedData = chartCycles.map<Data>((nomCycle) =>
        gasDates.reduce(
          (acc, gasDate) => {
            const key = `${gasDate}_${nomCycle}`;
            const values = rolledRows.map((row) => {
              return row.flowMap?.[key]?.[concept] ?? null;
            });
            const pooledValue = POOLING_FUNCTIONS[mode](
              ...(values.filter((value) => value !== null) as number[]),
            );
            const description =
              rolledRows[values.indexOf(pooledValue)]?.description ?? '';
            acc.x.push(gasDate);
            acc.y.push(pooledValue);
            acc.text.push(description);
            acc.hovertemplate.push(
              `<b>%{x}</b><br>` +
                `<b>Component:</b> %{text}<br>` +
                `<b>${POOLING_TEXTS[mode]} ${titleize(concept)}:</b> %{y}<br>`,
            );

            return acc;
          },
          {
            x: [] as string[],
            y: [] as number[],
            text: [] as string[],
            hovertemplate: [] as string[],
            type: 'scatter' as 'scatter',
            mode: 'lines' as 'lines',
            marker: { color: NOM_CYCLE_COLOR[nomCycle] },
            name: nomCycle,
          },
        ),
      );
      return aggregatedData;
    },
    [chartCycles, gasDates, rolledRows, concept],
  );

  const chartData = useMemo(() => {
    if (chartMode === 'raw') {
      return getRawChartData();
    } else {
      return getAggregatedChartData(chartMode);
    }
  }, [chartMode, getRawChartData, getAggregatedChartData]);

  const chartLayout = useMemo<Partial<Plotly.Layout>>(() => {
    const conceptTitle = titleize(concept.replaceAll('_', ' '));
    return {
      autosize: true,
      plot_bgcolor: colors.themes[theme].secondaryBg,
      paper_bgcolor: colors.themes[theme].body,
      title: {
        text: [
          chartMode === 'raw' ? '' : POOLING_TEXTS[chartMode],
          `${conceptTitle} Over Time`,
        ]
          .filter(Boolean)
          .join(' '),
      },
      font: { color: colors.themes[theme].color },
      xaxis: {
        title: { text: 'Gas Date' },
        color: colors.themes[theme].color,
      },
      yaxis: {
        title: { text: conceptTitle },
        color: colors.themes[theme].color,
      },
    };
  }, [theme, concept, chartMode]);

  if (isLoading) {
    return <LoadingIndicator className="my-5" style={{ overflow: 'hidden' }} />;
  }

  if (error) {
    console.error(error);
    toast.error(`${error}`);
    return null;
  }

  const toggleGroupExpanded = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const shouldShowEditComponent = (row: BulkFlowTableRow) => {
    if (!debug) return false;
    if (!canSeeMapBuilder) return false;
    if (!row._isRollupParent) return false;
    return Number.isFinite(Number(row.component_id));
  };

  const onEditComponentClick = (
    evt: React.MouseEvent<HTMLButtonElement>,
    row: BulkFlowTableRow,
  ) => {
    evt.stopPropagation();

    const componentId = Number(row.component_id || row.key?.component_id);
    if (!Number.isFinite(componentId)) {
      toast.error('Unable to determine capacity component id');
      return;
    }
    setEditingComponentId(componentId);
  };

  const getRowReasonTooltip = (row: BulkFlowTableRow) => {
    if (row.visible) {
      return '';
    }
    return row.reason || '';
  };

  const getNoticeBadgeLabel = (row: BulkFlowTableRow) => {
    const rawDirection = row.noticeDirection;
    if (!rawDirection) {
      return 'NOTICE DIR?';
    }

    if (rawDirection === 'CALCULATED') {
      return 'NOTICE CALC';
    }

    const dir = toDirectionShort(rawDirection);
    return dir ? `NOTICE ${dir}` : 'NOTICE DIR?';
  };

  const getNoticeBadgeTooltip = (row: BulkFlowTableRow) => {
    const rawDirection = row.noticeDirection;
    if (!rawDirection) {
      return 'No direction configured on notice component';
    }

    if (rawDirection === 'CALCULATED') {
      return 'Direction is calculated from flow context';
    }

    return `Notice direction: ${rawDirection}`;
  };

  const getNoticeBadgeClassName = (row: BulkFlowTableRow) => {
    const rawDirection = row.noticeDirection;
    if (!rawDirection) {
      return 'badge text-bg-warning ms-2';
    }

    if (rawDirection === 'CALCULATED') {
      return 'badge text-bg-info ms-2';
    }

    return 'badge text-bg-secondary ms-2';
  };

  const renderRowLabel = (row: BulkFlowTableRow) => (
    <span className="d-inline-flex align-items-center gap-1">
      {row._isRollupParent && row._childCount && row._groupKey ? (
        <button
          type="button"
          className="btn btn-link p-0 border-0 d-inline-flex align-items-center gap-1 text-decoration-none"
          style={{
            color:
              (row._childCount ?? 0) > 1
                ? 'var(--bs-secondary)'
                : 'var(--bs-secondary-color)',
            opacity: (row._childCount ?? 0) > 1 ? 1 : 0.35,
          }}
          onClick={() => toggleGroupExpanded(row._groupKey)}
          title={
            expandedGroups[row._groupKey]
              ? 'Collapse raw rows'
              : 'Expand raw rows'
          }
        >
          {/* {row._childCount > 1 && <span>{row._childCount}</span>} */}
          <span
            style={{
              display: 'inline-flex',
              transform: expandedGroups[row._groupKey]
                ? 'rotate(0deg)'
                : 'rotate(-90deg)',
              transition: 'transform 0.15s ease',
            }}
          >
            <ChevronDownIcon />
          </span>
        </button>
      ) : null}
      {row.source_kind === 'notice' ? (
        <ClipboardList size={16} />
      ) : row._isRawChild ? (
        <Gauge size={16} />
      ) : null}
      <span
        style={{
          paddingLeft: row._isRawChild || row._isNoticeChild ? 18 : 0,
        }}
      >
        {(row._isRawChild || row._isNoticeChild) && (
          <CornerDownRight
            size={16}
            className="me-1 text-secondary"
            style={{ display: 'inline' }}
          />
        )}
        {!row._isRawChild &&
          !row._isNoticeChild &&
          showTspName &&
          row.tsp_name && (
            <span
              className="text-muted me-2"
              style={{ fontSize: '0.72em', fontWeight: 'normal' }}
            >
              {row.tsp_name}
            </span>
          )}
        {getDisplayRowLabel(row)}
        {row._isRawChild && (
          <span
            className="badge text-bg-secondary ms-2"
            style={{
              fontSize: '0.62rem',
              letterSpacing: '0.04em',
            }}
            title={row.rawFlowDirection || ''}
          >
            {`RAW ${toDirectionShort(row.rawFlowDirection)}`.trim()}
          </span>
        )}
        {row._isNoticeChild && (
          <span
            className={getNoticeBadgeClassName(row)}
            style={{
              fontSize: '0.62rem',
              letterSpacing: '0.04em',
            }}
            title={getNoticeBadgeTooltip(row)}
          >
            {getNoticeBadgeLabel(row)}
          </span>
        )}
        {shouldShowEditComponent(row) && (
          <span className="ms-2">
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-secondary"
              onClick={(evt) => onEditComponentClick(evt, row)}
              title="Edit component"
            >
              <EditIcon />
            </button>
          </span>
        )}
      </span>
    </span>
  );

  const renderMatrixHeaderRows = () => (
    <>
      <tr>
        <th style={{ position: 'sticky', top: 0, left: 0, zIndex: 40 }}>
          Component
        </th>
        <th>Path Dir</th>
        {displayedGasDates.map((date) => (
          <th
            key={date}
            colSpan={getDisplayedCyclesForDate(date).length}
            style={{
              textAlign: 'center',
              borderRight: '1px solid var(--bs-border-color)',
              zIndex: 40,
            }}
          >
            {date}
          </th>
        ))}
        {hasMoreHistory && isLoadingMoreHistory && (
          <th rowSpan={2} className="text-center align-middle">
            Loading
          </th>
        )}
      </tr>

      <tr>
        <th
          className="bg-light"
          style={{
            position: 'sticky',
            left: 0,
            zIndex: 40,
          }}
        ></th>
        <th className="bg-light"></th>
        {displayedGasDates.flatMap((date) =>
          getDisplayedCyclesForDate(date).map((cycle) => {
            const sliceKey = `${date}_${cycle}`;
            const isSelected = selectedSliceKey === sliceKey;
            return (
              <th
                style={{
                  textAlign: 'center',
                  padding: '0.2rem 0.3rem',
                  backgroundColor: isSelected
                    ? 'var(--bs-warning-bg-subtle)'
                    : undefined,
                }}
                key={sliceKey}
                title={
                  isSelected
                    ? `Return to matrix view from ${date} ${cycle}`
                    : `View cycle detail: ${date} ${cycle}`
                }
              >
                <button
                  type="button"
                  className={`btn btn-sm ${
                    isSelected ? 'btn-warning' : 'btn-outline-secondary'
                  }`}
                  style={{
                    minWidth: 42,
                  }}
                  aria-pressed={isSelected}
                  onClick={() => onCycleToggle(sliceKey)}
                  title={
                    isSelected
                      ? `Return to matrix view from ${date} ${cycle}`
                      : `View cycle detail: ${date} ${cycle}`
                  }
                >
                  {cycle}
                </button>
              </th>
            );
          }),
        )}
      </tr>
    </>
  );

  const DETAIL_HEADERS = [
    'Flow Direction',
    'Scheduled',
    'OpCap',
    'OAC',
    'Flowable',
    'Utilization',
    'Included',
    'Reason',
    'Notices',
  ];

  const renderDetailHeaderRow = () => (
    <tr>
      <th style={{ position: 'sticky', top: 0, left: 0, zIndex: 40 }}>
        Component
      </th>
      <th>Path Direction</th>
      {DETAIL_HEADERS.map((label) => (
        <th key={label}>{label}</th>
      ))}
    </tr>
  );

  const renderStudySlicePicker = () => (
    <div className="border rounded p-2 mb-2 bg-tertiary flex-shrink-0">
      <div className="small text-muted mb-2 fw-semibold">
        Choose a date/cycle for Cycle Detail View
      </div>
      <div ref={studyPickerScrollRef} style={{ overflowX: 'auto' }}>
        <table
          className="table table-sm mb-0"
          style={{ width: 'max-content', borderCollapse: 'separate' }}
        >
          <thead>
            <tr>
              <th
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 40,
                  background: 'var(--bs-light-bg-subtle)',
                  minWidth: 220,
                }}
              />
              <th
                style={{
                  background: 'var(--bs-light-bg-subtle)',
                  minWidth: 130,
                }}
              />
              {visibleGasDates.map((date) => (
                <th
                  key={`study-date-${date}`}
                  colSpan={getCyclesForDate(date).length}
                  style={{
                    textAlign: 'center',
                    borderRight: '1px solid var(--bs-border-color)',
                    fontSize: '0.78rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {date}
                  {pendingCycleCount(date) > 0 && (
                    <span
                      className="badge text-bg-secondary ms-1"
                      style={{ fontSize: '0.65em', fontWeight: 'normal' }}
                      title={`${pendingCycleCount(
                        date,
                      )} cycle(s) not yet posted`}
                    >
                      +{pendingCycleCount(date)}
                    </span>
                  )}
                </th>
              ))}
            </tr>
            <tr>
              <th
                className="bg-light"
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 40,
                  minWidth: 220,
                }}
              />
              <th className="bg-light" style={{ minWidth: 130 }} />
              {visibleGasDates.flatMap((date) =>
                getCyclesForDate(date).map((cycle) => {
                  const sliceKey = `${date}_${cycle}`;
                  const isSelected = selectedSliceKey === sliceKey;
                  return (
                    <th
                      key={`study-cycle-${sliceKey}`}
                      style={{
                        textAlign: 'center',
                        padding: '0.2rem 0.3rem',
                        backgroundColor: isSelected
                          ? 'var(--bs-warning-bg-subtle)'
                          : undefined,
                      }}
                    >
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          isSelected ? 'btn-warning' : 'btn-outline-secondary'
                        }`}
                        id={cycleButtonId(sliceKey)}
                        style={{ minWidth: 42 }}
                        aria-pressed={isSelected}
                        onClick={() => onCycleToggle(sliceKey)}
                        title={
                          isSelected
                            ? `Return to matrix view from ${date} ${cycle}`
                            : `View cycle detail: ${date} ${cycle}`
                        }
                      >
                        {cycle}
                      </button>
                    </th>
                  );
                }),
              )}
            </tr>
          </thead>
        </table>
      </div>
    </div>
  );

  const renderMatrixCells = (row: BulkFlowTableRow) =>
    displayedGasDates.flatMap((date) =>
      getDisplayedCyclesForDate(date).map((cycle) => {
        const key = `${date}_${cycle}`;
        const noticeRecords = getRenderableNoticeRecords(
          row.noticeMap?.[key] || [],
        );
        const hasNotice = noticeRecords.length > 0;
        const noticeSummary = getNoticeSummaryText(
          noticeRecords as NoticeRecordListItem[],
        );
        const tooltipId = `notice_${row.domSafeRowKey}_${date}_${cycle}`;
        const firstNoticeRecordId = noticeRecords[0]?.id;

        const handleNoticeClick = () => {
          console.log('Notice cell clicked:', firstNoticeRecordId);
          if (firstNoticeRecordId != null) {
            setSelectedNoticeRecordId(firstNoticeRecordId);
          }
        };

        if (row.source_kind === 'notice') {
          return (
            <React.Fragment key={key}>
              <td
                id={tooltipId}
                onClick={hasNotice ? handleNoticeClick : undefined}
                style={{
                  cursor: hasNotice ? 'pointer' : 'default',
                  width: '90px',
                }}
                className="text-center"
              >
                {hasNotice
                  ? noticeSummary || `${noticeRecords.length} notice${noticeRecords.length === 1 ? '' : 's'}`
                  : '-'}
              </td>
              {hasNotice && (
                <UncontrolledTooltip
                  target={tooltipId}
                  placement="top"
                  innerClassName="mw-100"
                  innerstyle={{
                    width: 'max-content',
                    maxWidth: '500px',
                  }}
                >
                  {renderNoticeTooltip(noticeRecords as NoticeRecordListItem[])}
                </UncontrolledTooltip>
              )}
            </React.Fragment>
          );
        }

        const record = row.flowMap[key];
        const showFlowNoticeDot =
          hasNotice && row._isRollupParent && Boolean(record);
        const showFlowNoticeSummary =
          hasNotice && row._isRollupParent && !record;
        const showFlowNoticeDetails =
          showFlowNoticeDot || showFlowNoticeSummary;
        const isRawEvidenceRow =
          row._isRawChild || row.child_kind === 'raw_flow';
        return (
          <React.Fragment key={key}>
            <td
              id={showFlowNoticeDetails ? tooltipId : undefined}
              onClick={showFlowNoticeDetails ? handleNoticeClick : undefined}
              style={{
                backgroundColor: getColor(concept, record),
                width: '90px',
                cursor: showFlowNoticeDetails ? 'pointer' : 'default',
              }}
              className="text-center"
              title={isRawEvidenceRow ? row.reason || '' : record?.reason || ''}
            >
              <span className="d-inline-flex align-items-center gap-1">
                <span>
                  {record
                    ? formatConcept(
                        concept,
                        record?.[concept],
                        record?.direction_sign,
                      )
                    : showFlowNoticeSummary
                    ? noticeSummary ||
                      `${noticeRecords.length} notice${
                        noticeRecords.length === 1 ? '' : 's'
                      }`
                    : '-'}
                </span>
                {showFlowNoticeDot && renderNoticeDot()}
              </span>
            </td>
            {showFlowNoticeDetails && (
              <UncontrolledTooltip
                target={tooltipId}
                placement="top"
                innerClassName="mw-100"
                innerstyle={{
                  width: 'max-content',
                  maxWidth: '500px',
                }}
              >
                {renderNoticeTooltip(noticeRecords as NoticeRecordListItem[])}
              </UncontrolledTooltip>
            )}
          </React.Fragment>
        );
      }),
    );

  const renderDetailCells = (row: BulkFlowTableRow, sliceKey: string) => {
    const record = row.flowMap[sliceKey];
    const isRawEvidenceRow = row._isRawChild || row.child_kind === 'raw_flow';
    const noticeRecords = getRenderableNoticeRecords(
      row.noticeMap?.[sliceKey] || [],
    );
    const hasNotice = noticeRecords.length > 0;
    const noticeSummary = getNoticeSummaryText(
      noticeRecords as NoticeRecordListItem[],
    );
    const tooltipId = `notice_${row.domSafeRowKey}_${sliceKey}`;
    const firstNoticeRecordId = noticeRecords[0]?.id;

    const handleNoticeClick = () => {
      if (firstNoticeRecordId != null) {
        setSelectedNoticeRecordId(firstNoticeRecordId);
      }
    };

    return (
      <>
        <td>{record?.flow_direction || '-'}</td>
        <td>
          {formatConcept(
            'scheduled_volume',
            record?.scheduled_volume,
            record?.direction_sign,
          )}
        </td>
        <td>{formatConcept('opcap', record?.opcap, record?.direction_sign)}</td>
        <td>{formatConcept('oac', record?.oac, record?.direction_sign)}</td>
        <td>
          {formatConcept(
            'flowable_volume',
            record?.flowable_volume,
            record?.direction_sign,
          )}
        </td>
        <td>
          {formatConcept(
            'utilization',
            record?.utilization,
            record?.direction_sign,
          )}
        </td>
        <td>
          {isRawEvidenceRow
            ? '-'
            : record
            ? record.included
              ? 'Yes'
              : 'No'
            : '-'}
        </td>
        <td>
          {isRawEvidenceRow
            ? row.reason || '-'
            : record?.reason || row.reason || '-'}
        </td>
        <td
          id={hasNotice ? tooltipId : undefined}
          onClick={hasNotice ? handleNoticeClick : undefined}
          style={{
            textAlign: 'center',
            cursor: hasNotice ? 'pointer' : 'default',
            maxWidth: 180,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={
            hasNotice
              ? noticeSummary || `${noticeRecords.length} notice${noticeRecords.length === 1 ? '' : 's'}`
              : undefined
          }
        >
          {hasNotice
            ? noticeSummary || `${noticeRecords.length} notice${noticeRecords.length === 1 ? '' : 's'}`
            : '-'}
          {hasNotice && (
            <UncontrolledTooltip
              target={tooltipId}
              placement="top"
              innerClassName="mw-100"
              innerstyle={{
                width: 'max-content',
                maxWidth: '500px',
              }}
            >
              {renderNoticeTooltip(noticeRecords as NoticeRecordListItem[])}
            </UncontrolledTooltip>
          )}
        </td>
      </>
    );
  };

  const selectedSlice = selectedSliceKey
    ? parseSliceKey(selectedSliceKey)
    : null;

  const onCycleToggle = (sliceKey: string) => {
    setSelectedSliceKey((prev) => {
      if (prev === sliceKey) {
        return null;
      }

      setLastMatrixScrollLeft(matrixScrollRef.current?.scrollLeft || 0);
      return sliceKey;
    });
  };

  const onMatrixScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const matrix = event.currentTarget;
    const isAtOldestDateEdge =
      matrix.scrollLeft + matrix.clientWidth >= matrix.scrollWidth - 80;
    if (
      !selectedSliceKey &&
      hasMoreHistory &&
      !isLoadingMoreHistory &&
      isAtOldestDateEdge
    ) {
      onLoadMoreHistory?.();
    }
  };

  React.useEffect(() => {
    if (!selectedSliceKey) {
      return;
    }

    requestAnimationFrame(() => {
      if (studyPickerScrollRef.current) {
        studyPickerScrollRef.current.scrollLeft = lastMatrixScrollLeft;
      }

      const button = document.getElementById(cycleButtonId(selectedSliceKey));
      button?.scrollIntoView({ block: 'nearest', inline: 'center' });
    });
  }, [lastMatrixScrollLeft, selectedSliceKey]);

  return (
    <div
      ref={containerRef}
      className="d-flex flex-column"
      style={{ maxHeight: 'calc(100vh - 160px)', minHeight: 0 }}
    >
      <div className="d-flex align-items-center gap-2 mb-2 flex-shrink-0">
        <select
          className="form-select"
          value={concept}
          onChange={(evt) =>
            setConcept(evt.target.value as keyof PathEvaluatedFlowRecord)
          }
        >
          <option value="flowable_volume">Flowable Volume</option>
          <option value="utilization">Utilization</option>
          <option value="oac">OAC</option>
          <option value="scheduled_volume">Scheduled Volume</option>
          <option value="opcap">Op Cap</option>
          <option value="interruptible_flow">Interruptible Flow?</option>
        </select>
        <Button
          type="button"
          color={debug ? 'warning' : 'secondary'}
          outline={!debug}
          active={debug}
          title={
            debug
              ? 'Debug mode on: excluded rows are visible'
              : 'Debug mode off'
          }
          aria-pressed={debug}
          onClick={() => setDebug((prev) => !prev)}
        >
          <Bug />
        </Button>
        <Button
          type="button"
          color="secondary"
          outline
          title="Copy flow table for Excel"
          onClick={copyMatrixToClipboard}
        >
          <Copy className="me-1" />
        </Button>
        {selectedSliceKey && selectedSlice && (
          <>
            <Button
              type="button"
              color="secondary"
              onClick={() => setSelectedSliceKey(null)}
              title="Back to full matrix"
            >
              Back to Matrix
            </Button>
          </>
        )}
      </div>
      {historyViewSettings && !selectedSliceKey && (
        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap flex-shrink-0">
          <div className="btn-group btn-group-sm" role="group" aria-label="History view">
            <button
              type="button"
              className={`btn ${
                isFocusedView ? 'btn-primary' : 'btn-outline-primary'
              }`}
              aria-pressed={isFocusedView}
              onClick={() => {
                setHistoryScope('focused');
                onHistoryScopeChange?.('focused');
              }}
            >
              Focused Date
            </button>
            <button
              type="button"
              className={`btn ${
                !isFocusedView ? 'btn-primary' : 'btn-outline-primary'
              }`}
              aria-pressed={!isFocusedView}
              onClick={() => {
                setHistoryScope('history');
                onHistoryScopeChange?.('history');
              }}
            >
              History
            </button>
          </div>
          {isFocusedView && (
            <>
              <label className="small text-muted mb-0" htmlFor="focused-flow-date">
                Date
              </label>
              <select
                id="focused-flow-date"
                className="form-select form-select-sm"
                value={focusedDate || ''}
                onChange={(event) => {
                  setFocusedDate(event.target.value);
                  setFocusedCycle('all');
                }}
                style={{ width: 126 }}
              >
                {visibleGasDates.map((date) => (
                  <option key={date} value={date}>
                    {date}
                  </option>
                ))}
              </select>
              <label className="small text-muted mb-0" htmlFor="focused-flow-cycle">
                Cycles
              </label>
              <select
                id="focused-flow-cycle"
                className="form-select form-select-sm"
                value={focusedCycle}
                onChange={(event) =>
                  setFocusedCycle(event.target.value as 'all' | NomCycle)
                }
                style={{ width: 132 }}
              >
                <option value="all">All available</option>
                {focusedDateCycles.map((cycle) => (
                  <option key={cycle} value={cycle}>
                    {cycle}
                  </option>
                ))}
              </select>
              {laterFocusedCycles.length > 0 && (
                <span
                  className="badge text-bg-warning"
                  title={`Later cycles available: ${laterFocusedCycles.join(', ')}`}
                >
                  Later cycles: {laterFocusedCycles.join(', ')}
                </span>
              )}
            </>
          )}
        </div>
      )}
      {selectedSliceKey && renderStudySlicePicker()}
      {!selectedSliceKey && (
        <>
          <div
            style={{
              display: chartOpen ? 'block' : 'none',
            }}
          >
            <div
              className="d-flex justify-content-end gap-2"
              style={{
                zIndex: 50,
              }}
            >
              <div className="input-group">
                <label className="input-group-text" htmlFor="chart-mode-select">
                  Chart mode
                </label>
                <select
                  id="chart-mode-select"
                  onChange={(evt) =>
                    setChartMode(evt.target.value as 'raw' | 'min' | 'max')
                  }
                  value={chartMode}
                  className="form-select"
                  style={{
                    maxWidth: '256px',
                  }}
                >
                  <option value="raw">Raw</option>
                  <option value="min">Min</option>
                  <option value="max">Max</option>
                </select>
              </div>
              <MultiSelectButton
                // style={{ float: 'right', width: '300px' }}
                options={NOM_CYCLES}
                isMarked={(cycle) => chartCycles.includes(cycle)}
                onToggle={(cycle, checked) => {
                  setChartCycles((prev) => {
                    if (checked) {
                      return [...prev, cycle];
                    } else {
                      return prev.filter((c) => c !== cycle);
                    }
                  });
                }}
                keyExtractor={(cycle) => cycle}
                optionRenderer={(cycle) => cycle}
                title="Nomination Cycles"
              />
            </div>
            <Plot
              data={chartData}
              layout={chartLayout}
              config={{ scrollZoom: true }}
              className="w-100"
            />
          </div>
          <HorizontalDivider
            className="my-4"
            color="var(--bs-secondary-bg)"
            onClick={toggleChart}
          >
            {chartOpen ? <ChevronUpIcon /> : <TrendingUp size={16} />}
          </HorizontalDivider>
        </>
      )}

      <div
        ref={matrixScrollRef}
        className=" flex-grow-1"
        style={{ minHeight: 0, overflowX: 'scroll', overflowY: 'auto' }}
        onScroll={onMatrixScroll}
      >
        <table
          className="table table-striped mb-0"
          style={{
            width: 'max-content',
            borderCollapse: 'separate',
            borderSpacing: 0,
          }}
        >
          <thead
            className="bg-light"
            style={{ position: 'sticky', top: 0, zIndex: 20 }}
          >
            {!selectedSliceKey
              ? renderMatrixHeaderRows()
              : renderDetailHeaderRow()}
          </thead>

          <tbody>
            {rolledRows.map((row, rowIndex) => (
              <tr
                key={row.rowKey}
                className={!row.visible ? 'opacity-50' : ''}
                style={
                  row._isRawChild || row._isNoticeChild
                    ? {
                        backgroundColor: 'var(--bs-secondary-bg)',
                        color: 'var(--bs-secondary-color)',
                        boxShadow: 'inset 4px 0 0 var(--bs-secondary-color)',
                        opacity: 0.78,
                      }
                    : undefined
                }
              >
                <td
                  style={{
                    position: 'sticky',
                    left: 0,
                    background:
                      row._isRawChild || row._isNoticeChild
                        ? 'var(--bs-secondary-bg)'
                        : 'var(--bs-table-bg)',
                    zIndex: 1,
                    fontWeight: 'bold',
                  }}
                  title={getRowReasonTooltip(row)}
                >
                  {renderRowLabel(row)}
                </td>
                <td>
                  {toDirectionShort(
                    row.path_direction as
                      | PointFlowDirection
                      | SegmentFlowDirection,
                  )}
                </td>

                {!selectedSliceKey
                  ? renderMatrixCells(row)
                  : renderDetailCells(row, selectedSliceKey)}
                {!selectedSliceKey &&
                  hasMoreHistory &&
                  isLoadingMoreHistory &&
                  rowIndex === 0 && (
                    <td
                      rowSpan={rolledRows.length}
                      className="align-middle text-center"
                      style={{ minWidth: 132 }}
                    >
                      <div
                        className="d-flex flex-column align-items-center gap-2 small text-muted"
                        aria-live="polite"
                      >
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                        Loading older history...
                      </div>
                    </td>
                  )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EditCapacityComponentModal
        component={
          editingComponentId !== null ? editingComponent ?? null : null
        }
        onClose={() => setEditingComponentId(null)}
        onSave={onSave}
      />
      <NoticeRecordModal
        noticeRecordId={selectedNoticeRecordId}
        isOpen={selectedNoticeRecordId !== null}
        onClose={() => setSelectedNoticeRecordId(null)}
      />
    </div>
  );
};
